import { axiosInstanceWebsite } from "@/services/instance";
import type { CompanyData, FixedCost } from "../components/dashboard/company/types";
import type { DateRange, Funnel, MetaCampaign } from "../components/dashboard/funnels/types";
import type { ChatConversation, ChatConversationDetail, ChatRequest, ChatResponse } from "../components/ai-chat/types";
import type { FunnelDetailData, Period } from "../components/funnel-detail/types";
import { formatDateForApi } from "../components/funnel-detail/periods";

export type FinanceStatusResponse = {
  backend_available?: boolean;
  finance_enabled?: boolean;
  finance_activated_at?: string | null;
  meta_connected?: boolean;
};

export type FinanceMetaConnectionResponse = {
  connected: boolean;
  status: string;
  business_id?: string | null;
  business_name?: string | null;
  selected_ad_account_id?: string | null;
  selected_ad_account_name?: string | null;
  connected_at?: string | null;
  token_expires_at?: string | null;
  last_error?: string | null;
};

type FinanceMetaOAuthUrlApiResponse = {
  authorization_url?: string;
  authorizationUrl?: string;
};

export type FinanceMetaOAuthUrlResponse = {
  authorizationUrl: string;
};

type FinanceFixedCostsResponse = {
  currency?: string;
  sourceRowsCount?: number;
  groupedCostsCount?: number;
  costs: FixedCost[];
};

export type FinanceMetaAdAccount = {
  id: string;
  name?: string;
  account_id?: string | null;
  account_status?: number | string | null;
  currency?: string | null;
  timezone_name?: string | null;
};

type FinanceMetaAdAccountsApiResponse = {
  ok?: boolean;
  adaccounts?: FinanceMetaAdAccount[];
  data?: FinanceMetaAdAccount[];
};

export type FinanceMetaBackfillResponse = {
  ok: boolean;
  status: string;
  sync_run_id?: number | null;
  syncRunId?: number | null;
  message?: string | null;
  auto_match?: FinanceAutoMatchSummary | null;
  autoMatch?: FinanceAutoMatchSummary | null;
};

export type FinanceAutoMatchSummary = {
  funnels_detected?: number;
  funnelsDetected?: number;
  meta_campaigns_matched?: number;
  metaCampaignsMatched?: number;
  meta_funnels_matched?: number;
  metaFunnelsMatched?: number;
  dropi_orders_matched?: number;
  dropiOrdersMatched?: number;
  dropi_orders_unmatched?: number;
  dropiOrdersUnmatched?: number;
  pending_funnels?: number;
  pendingFunnels?: number;
};

export type FinanceDropiBackfillResponse = {
  ok: boolean;
  status: string;
  sync_run_id?: number | null;
  syncRunId?: number | null;
  orders_count: number;
  ordersCount?: number;
  matched_orders_count?: number;
  matchedOrdersCount?: number;
  unmatched_orders_count?: number;
  unmatchedOrdersCount?: number;
  partial?: boolean;
  rate_limited_segments?: number;
  rateLimitedSegments?: number;
  failed_segments?: number;
  failedSegments?: number;
  message: string;
};

export type FinanceMetaCampaignPerformance = {
  id: string;
  name: string;
  ad_account_id?: string | null;
  objective?: string | null;
  effective_status?: string | null;
  daily_budget?: number | null;
  lifetime_budget?: number | null;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  matched_funnel_ids: string[];
};

export type FinanceMetaAdPerformance = {
  id: string;
  name: string;
  ad_account_id?: string | null;
  campaign_id: string;
  campaign_name?: string | null;
  spend: number;
  impressions: number;
  reach: number;
  frequency: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_result: number;
  last_activity?: string | null;
};

type FinanceCampaignRow = {
  id: string;
  name: string;
  status: "ACTIVE" | "PAUSED" | "OTHER";
  spend: number;
  revenue: number;
  purchases: number;
  roas: number;
  cxc: number;
  ctr: number;
  lastActivity: string;
};

type MetaAdRow = {
  ad_id?: string;
  ad_name?: string;
  campaign_name?: string;
  effective_status?: string;
  spend?: number;
  purchases?: number;
  roas?: number;
  cost_per_result?: number;
  ctr?: number;
  results?: number;
  updated_time?: string;
};

type FinanceFunnelApiRow = {
  id: string;
  name: string;
  product_name?: string;
  productName?: string;
  product_image_url?: string;
  productImageUrl?: string;
  is_active?: boolean;
  isActive?: boolean;
  is_archived?: boolean;
  isArchived?: boolean;
  id_sequence_owner_id?: number | null;
  idSequenceOwnerId?: number | null;
  meta_status?: Funnel["metaStatus"];
  metaStatus?: Funnel["metaStatus"];
  metrics?: {
    profit?: number | null;
    sales_count?: number | null;
    salesCount?: number | null;
    sales_value?: number | null;
    salesValue?: number | null;
    cpa?: number | null;
  };
  matched_campaign_ids?: string[];
  matchedCampaignIds?: string[];
  manual_logistics_configured?: boolean;
  manualLogisticsConfigured?: boolean;
};

type FinanceCompanySummaryResponse = Partial<CompanyData>;
type FinanceFunnelDetailResponse = FunnelDetailData;

export type FinanceManualLogisticsConfig = {
  configured: boolean;
  funnel_id: string;
  product_cost: number;
  shipping_cost: number;
  fulfillment_cost: number;
  platform_commission: number;
  return_rate: number;
  currency: string;
};

export type FinanceLogisticsSummary = {
  total_orders: number;
  dropi_orders: number;
  matched_orders: number;
  unmatched_orders: number;
  matched_value: number;
  unmatched_value: number;
  matched_product_cost: number;
  unmatched_product_cost: number;
  matched_shipping_cost: number;
  unmatched_shipping_cost: number;
  with_guide: number;
  in_transit: number;
  delivered: number;
  returned: number;
  incidents: number;
  delivery_rate: number;
  return_rate: number;
  value_at_risk: number;
  product_cost: number;
  shipping_cost: number;
  fulfillment_cost: number;
  platform_commission: number;
  return_cost: number;
  variable_cost: number;
  currency: string;
};

export type FinanceLogisticsStatusBucket = {
  id: string;
  label: string;
  count: number;
  value: number;
};

export type FinanceLogisticsMapOrder = {
  order_id: number;
  order_hash_id?: string | null;
  status: string;
  status_label: string;
  city?: string | null;
  department?: string | null;
  latitude: number;
  longitude: number;
  carrier?: string | null;
  guide_number?: string | null;
  value: number;
};

export type FinanceLogisticsMapBubble = {
  key: string;
  label: string;
  status: string;
  status_label: string;
  city?: string | null;
  department?: string | null;
  latitude: number;
  longitude: number;
  count: number;
  value: number;
};

export type FinanceLogisticsOrder = {
  order_id: number;
  order_hash_id?: string | null;
  funnel_id?: string | null;
  status: string;
  status_label: string;
  order_state?: string | null;
  logistic_state?: string | null;
  dropi_state?: string | null;
  dropi_order_id?: number | null;
  guide_number?: string | null;
  carrier?: string | null;
  sticker_file?: string | null;
  customer_name?: string | null;
  city?: string | null;
  department?: string | null;
  product_names?: string | null;
  units: number;
  value: number;
  total_paid?: number;
  totalPaid?: number;
  product_cost?: number;
  shipping_cost?: number;
  fulfillment_cost?: number;
  platform_commission?: number;
  return_cost?: number;
  variable_cost?: number;
  currency: string;
  created_at: string;
  last_tracking_at?: string | null;
  last_error?: string | null;
};

export type FinanceLogisticsCarrierSource = {
  id: string;
  name: string;
  status: string;
  tracking_source: string;
  notes: string;
  docs_url?: string | null;
};

export type FinanceLogisticsOverview = {
  dropi_connected: boolean;
  dropi_status: string;
  period: Period;
  summary: FinanceLogisticsSummary;
  status_breakdown: FinanceLogisticsStatusBucket[];
  map_orders: FinanceLogisticsMapOrder[];
  map_bubbles?: FinanceLogisticsMapBubble[];
  orders: FinanceLogisticsOrder[];
  carrier_sources: FinanceLogisticsCarrierSource[];
};

export type FinanceReportChannel = "email" | "whatsapp";

export type FinanceReportRecipient = {
  label: string;
  email?: string;
  phone?: string;
};

export type FinanceReportRequest = {
  period: Period;
  frequency: string;
  send_time: string;
  channels: FinanceReportChannel[];
  sections: string[];
  recipients: FinanceReportRecipient[];
};

export type FinanceReportConfig = {
  configured: boolean;
  frequency: string;
  send_time: string;
  country_code: string;
  channels: FinanceReportChannel[];
  sections: string[];
  recipients: FinanceReportRecipient[];
};

export type FinanceReportDelivery = {
  channel: FinanceReportChannel;
  status: "ready" | "sent" | "pending_provider" | "missing_recipient" | "failed";
  message: string;
};

export type FinanceReportPreview = {
  ok: boolean;
  title: string;
  executive_summary: string;
  sections: Array<{
    id: string;
    title: string;
    body: string;
    metric?: string;
  }>;
  deliveries: FinanceReportDelivery[];
};

export const getFinanceStatus = async (): Promise<FinanceStatusResponse> => {
  try {
    const response = await axiosInstanceWebsite.get<FinanceStatusResponse>("/finance/status");
    return {
      backend_available: true,
      finance_enabled: Boolean(response.data.finance_enabled),
      finance_activated_at: response.data.finance_activated_at ?? null,
      meta_connected: Boolean(response.data.meta_connected),
    };
  } catch {
    return {
      backend_available: false,
      finance_enabled: false,
      finance_activated_at: null,
      meta_connected: false,
    };
  }
};

export const activateFinance = async (): Promise<FinanceStatusResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceStatusResponse>("/finance/activate");
  return {
    backend_available: true,
    finance_enabled: Boolean(response.data.finance_enabled),
    finance_activated_at: response.data.finance_activated_at ?? null,
    meta_connected: Boolean(response.data.meta_connected),
  };
};

export const getFinanceMetaConnection = async (): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaConnectionResponse>(
    "/finance/meta/connection"
  );
  return response.data;
};

export const getFinanceMetaOAuthUrl = async (): Promise<FinanceMetaOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaOAuthUrlApiResponse>(
    "/finance/meta/oauth/url"
  );
  return {
    authorizationUrl:
      response.data.authorization_url ??
      response.data.authorizationUrl ??
      "",
  };
};

export const getFinanceMetaAdAccounts = async (businessId?: string): Promise<FinanceMetaAdAccount[]> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaAdAccountsApiResponse>(
    "/finance/meta/ad-accounts",
    { params: businessId ? { business_id: businessId } : undefined }
  );
  return response.data.adaccounts ?? response.data.data ?? [];
};

export const updateFinanceMetaAdAccount = async (
  adAccountId: string,
  adAccountName?: string
): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceMetaConnectionResponse>(
    "/finance/meta/ad-account",
    {
      ad_account_id: adAccountId,
      ad_account_name: adAccountName,
    }
  );
  return response.data;
};

export const getFunnels = async (dateRange?: DateRange): Promise<Funnel[]> => {
  const params = dateRange
    ? {
        period: "custom",
        from: formatDateForApi(dateRange.initialDate),
        to: formatDateForApi(dateRange.finalDate),
      }
    : undefined;
  const response = await axiosInstanceWebsite.get<FinanceFunnelApiRow[]>("/finance/funnels", {
    params,
  });
  return response.data.map((item) => ({
    id: item.id,
    name: item.name,
    productName: item.product_name ?? item.productName ?? "Producto sin nombre",
    productImageUrl: item.product_image_url ?? item.productImageUrl ?? "",
    isActive: item.is_active ?? item.isActive ?? true,
    isArchived: item.is_archived ?? item.isArchived ?? false,
    idSequenceOwnerId: item.id_sequence_owner_id ?? item.idSequenceOwnerId ?? null,
    metaStatus: item.meta_status ?? item.metaStatus ?? "unconfigured",
    metrics: {
      profit: item.metrics?.profit ?? null,
      salesCount: item.metrics?.sales_count ?? item.metrics?.salesCount ?? null,
      salesValue: item.metrics?.sales_value ?? item.metrics?.salesValue ?? null,
      cpa: item.metrics?.cpa ?? null,
    },
    matchedCampaignIds: item.matched_campaign_ids ?? item.matchedCampaignIds ?? [],
    manualLogisticsConfigured: Boolean(
      item.manual_logistics_configured ?? item.manualLogisticsConfigured
    ),
  }));
};

export const listFinanceFunnels = getFunnels;

export const getMetaCampaigns = async (): Promise<MetaCampaign[]> => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const response = await axiosInstanceWebsite.get<{
    items: Array<{
      id: string;
      name: string;
      effective_status: string;
      spend: number;
      roas: number;
    }>;
  }>("/finance/meta/campaigns", {
    params: { from: formatDate(from), to: formatDate(to) },
  });

  return response.data.items.map((item) => ({
    id: item.id,
    name: item.name,
    status: item.effective_status === "ACTIVE" ? "ACTIVE" : "PAUSED",
    spend: Number(item.spend ?? 0),
    roas: item.roas === 0 && item.spend === 0 ? null : Number(item.roas ?? 0),
  }));
};

export const matchCampaignsToFunnel = async (
  funnelId: string,
  campaignIds: string[]
): Promise<void> => {
  await axiosInstanceWebsite.post(
    `/finance/funnels/${encodeURIComponent(funnelId)}/match-campaigns`,
    { campaign_ids: campaignIds }
  );
};

const buildPeriodParams = (period: Period) => {
  if (period.id === "all-time") {
    return { period: period.id };
  }

  return {
    period: period.id,
    from: formatDateForApi(period.from),
    to: formatDateForApi(period.to),
  };
};

export const getFinanceCampaignPerformance = async (
  period: Period
): Promise<FinanceMetaCampaignPerformance[]> => {
  const response = await axiosInstanceWebsite.get<{
    items?: FinanceMetaCampaignPerformance[];
  }>("/finance/meta/campaigns", {
    params: buildPeriodParams(period),
  });

  return (response.data.items ?? []).map((item) => ({
    ...item,
    spend: Number(item.spend ?? 0),
    impressions: Number(item.impressions ?? 0),
    clicks: Number(item.clicks ?? 0),
    purchases: Number(item.purchases ?? 0),
    purchase_value: Number(item.purchase_value ?? 0),
    roas: Number(item.roas ?? 0),
    matched_funnel_ids: item.matched_funnel_ids ?? [],
  }));
};

export const getFinanceAdsPerformance = async (
  period: Period
): Promise<FinanceMetaAdPerformance[]> => {
  const response = await axiosInstanceWebsite.get<{
    items?: FinanceMetaAdPerformance[];
  }>("/finance/meta/ads", {
    params: buildPeriodParams(period),
  });

  return (response.data.items ?? []).map((item) => ({
    ...item,
    spend: Number(item.spend ?? 0),
    impressions: Number(item.impressions ?? 0),
    reach: Number(item.reach ?? 0),
    frequency: Number(item.frequency ?? 0),
    clicks: Number(item.clicks ?? 0),
    purchases: Number(item.purchases ?? 0),
    purchase_value: Number(item.purchase_value ?? 0),
    roas: Number(item.roas ?? 0),
    ctr: Number(item.ctr ?? 0),
    cpc: Number(item.cpc ?? 0),
    cpm: Number(item.cpm ?? 0),
    cost_per_result: Number(item.cost_per_result ?? 0),
  }));
};

const emptyCompanyStages = (): CompanyData["stages"] => ({
  checkoutViews: 0,
  ordersCreated: 0,
  dispatched: 0,
  delivered: 0,
  returned: 0,
  ordersValue: 0,
  dispatchedValue: 0,
  deliveredValue: 0,
  returnedValue: 0,
});

const normalizeCompanySummary = (summary: FinanceCompanySummaryResponse): CompanyData => {
  const fixedCosts = summary.fixedCosts ?? [];
  const fixedCostsCurrency = summary.fixedCostsCurrency ?? fixedCosts[0]?.currency ?? "COP";
  const totalFixedCosts =
    summary.totalFixedCosts ?? fixedCosts.reduce((acc, cost) => acc + cost.amount, 0);
  const operatingProfit = summary.operatingProfit ?? 0;
  const paymentSummary = summary.revenueByPaymentMethod ?? {
    total: summary.totalRevenue ?? 0,
    cashOnDelivery: 0,
    prepaid: 0,
    other: 0,
    collected: 0,
  };
  const cpaTargets = summary.cpaTargets ?? {
    realCpa: summary.avgCpa ?? 0,
    targetCpa20: 0,
    breakEvenCpa: summary.avgMaxProfitableCpa ?? 0,
  };
  const totalProductCosts = Number(summary.totalProductCosts ?? 0);
  const totalVariableCosts = Number(summary.totalVariableCosts ?? 0);
  const rawTotalLogisticsCosts = Number(summary.totalLogisticsCosts ?? 0);
  const totalLogisticsCosts = rawTotalLogisticsCosts > 0 &&
    totalVariableCosts > 0 &&
    totalProductCosts > 0 &&
    Math.abs(rawTotalLogisticsCosts - totalVariableCosts) < 1
      ? Math.max(totalVariableCosts - totalProductCosts, 0)
      : rawTotalLogisticsCosts || (totalVariableCosts > 0 ? Math.max(totalVariableCosts - totalProductCosts, 0) : 0);
  const totalShippingCosts = Number(
    summary.totalShippingCosts ??
      Math.max(totalLogisticsCosts - Number(summary.totalFulfillmentCosts ?? 0) - Number(summary.totalPlatformCommission ?? 0) - Number(summary.totalReturnCosts ?? 0), 0)
  );
  const estimatedPendingProfit = Number(summary.estimatedPendingProfit ?? 0);

  return {
    period: summary.period,
    netProfit: summary.netProfit ?? operatingProfit - totalFixedCosts,
    operatingProfit,
    avgRoas: summary.avgRoas ?? 0,
    avgCpa: summary.avgCpa ?? 0,
    totalRevenue: summary.totalRevenue ?? 0,
    grossRevenue: summary.grossRevenue ?? summary.revenueByPaymentMethod?.total ?? 0,
    collectedRevenue: summary.collectedRevenue ?? summary.totalRevenue ?? 0,
    totalVariableCosts: totalVariableCosts || totalProductCosts + totalLogisticsCosts,
    totalLogisticsCosts,
    totalProductCosts,
    totalShippingCosts,
    totalFulfillmentCosts: Number(summary.totalFulfillmentCosts ?? 0),
    totalPlatformCommission: Number(summary.totalPlatformCommission ?? 0),
    totalReturnCosts: Number(summary.totalReturnCosts ?? 0),
    pendingRevenue: Number(summary.pendingRevenue ?? 0),
    pendingProductCosts: Number(summary.pendingProductCosts ?? 0),
    pendingLogisticsCosts: Number(summary.pendingLogisticsCosts ?? 0),
    estimatedPendingProfit,
    estimatedTotalProfit: Number(summary.estimatedTotalProfit ?? (summary.netProfit ?? operatingProfit - totalFixedCosts) + estimatedPendingProfit),
    totalAdInvestment: summary.totalAdInvestment ?? 0,
    matchedAdInvestment: summary.matchedAdInvestment ?? summary.totalAdInvestment ?? 0,
    unassignedAdInvestment: summary.unassignedAdInvestment ?? 0,
    fixedCosts: fixedCosts.map((cost) => ({ ...cost, currency: cost.currency ?? fixedCostsCurrency })),
    fixedCostsCurrency,
    totalFixedCosts,
    revenueByPaymentMethod: {
      total: Number(paymentSummary.total ?? 0),
      cashOnDelivery: Number(paymentSummary.cashOnDelivery ?? 0),
      prepaid: Number(paymentSummary.prepaid ?? 0),
      other: Number(paymentSummary.other ?? 0),
      collected: Number(paymentSummary.collected ?? 0),
    },
    funnels: (summary.funnels ?? []).map((funnel) => ({
      ...funnel,
      checkoutViews: Number(funnel.checkoutViews ?? 0),
    })),
    stages: summary.stages ?? emptyCompanyStages(),
    dailyData: summary.dailyData ?? [],
    cpaTargets: {
      realCpa: Number(cpaTargets.realCpa ?? 0),
      targetCpa20: Number(cpaTargets.targetCpa20 ?? 0),
      breakEvenCpa: Number(cpaTargets.breakEvenCpa ?? 0),
    },
    avgMaxProfitableCpa: summary.avgMaxProfitableCpa ?? 0,
  };
};

export const getCompanyFinanceSummary = async (period: Period): Promise<CompanyData> => {
  const response = await axiosInstanceWebsite.get<FinanceCompanySummaryResponse>(
    "/finance/company/summary",
    { params: buildPeriodParams(period) }
  );
  return normalizeCompanySummary(response.data);
};

const buildFixedCostsPayload = (costs: FixedCost[], currency = "COP") => ({
  currency,
  costs: costs.map((cost) => ({
    label: cost.label,
    amount: Number(cost.amount ?? 0),
  })),
});

export const saveFinanceFixedCosts = async (costs: FixedCost[], currency = "COP"): Promise<FixedCost[]> => {
  const response = await axiosInstanceWebsite.put<FinanceFixedCostsResponse>(
    "/finance/company/fixed-costs",
    buildFixedCostsPayload(costs, currency)
  );
  const responseCurrency = response.data.currency ?? currency;
  return (response.data.costs ?? []).map((cost) => ({ ...cost, currency: cost.currency ?? responseCurrency }));
};

export const previewFinanceFixedCostsImport = async (
  fileName: string,
  tableText: string,
  currency = "COP",
  sourceRowsCount = 0
): Promise<FinanceFixedCostsResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceFixedCostsResponse>(
    "/finance/company/fixed-costs/imports/preview",
    {
      file_name: fileName,
      table_text: tableText,
      currency,
      source_rows_count: sourceRowsCount,
    }
  );
  return {
    ...response.data,
    currency: response.data.currency ?? currency,
    costs: (response.data.costs ?? []).map((cost) => ({
      ...cost,
      currency: cost.currency ?? response.data.currency ?? currency,
    })),
  };
};

export const getFunnelFinanceDetail = async (
  funnelId: string,
  period: Period
): Promise<FunnelDetailData> => {
  const response = await axiosInstanceWebsite.get<FinanceFunnelDetailResponse>(
    `/finance/funnels/${encodeURIComponent(funnelId)}/detail`,
    { params: buildPeriodParams(period) }
  );
  return response.data;
};

export const sendFinanceAssistantMessage = async (
  request: ChatRequest
): Promise<ChatResponse> => {
  const response = await axiosInstanceWebsite.post<ChatResponse>(
    "/finance/assistant/messages",
    request
  );
  return response.data;
};

export const getFinanceAssistantConversations = async (): Promise<ChatConversation[]> => {
  const response = await axiosInstanceWebsite.get<{ conversations: ChatConversation[] }>(
    "/finance/assistant/conversations"
  );
  return response.data.conversations ?? [];
};

export const getFinanceAssistantConversation = async (
  conversationId: string
): Promise<ChatConversationDetail> => {
  const response = await axiosInstanceWebsite.get<ChatConversationDetail>(
    `/finance/assistant/conversations/${encodeURIComponent(conversationId)}`
  );
  return response.data;
};

const buildReportPayload = (request: FinanceReportRequest) => ({
  period: request.period.id,
  from: formatDateForApi(request.period.from),
  to: formatDateForApi(request.period.to),
  frequency: request.frequency,
  send_time: request.send_time,
  channels: request.channels,
  sections: request.sections,
  recipients: request.recipients,
});

const normalizeReportConfig = (config: FinanceReportConfig): FinanceReportConfig => ({
  configured: Boolean(config.configured),
  frequency: config.frequency ?? "",
  send_time: config.send_time ?? "",
  country_code: config.country_code ?? "CO",
  channels: config.channels?.length ? config.channels : ["email"],
  sections: config.sections ?? [],
  recipients: config.recipients?.length ? config.recipients : [{ label: "", email: "", phone: "" }],
});

export const getFinanceReportConfig = async (): Promise<FinanceReportConfig> => {
  const response = await axiosInstanceWebsite.get<FinanceReportConfig>("/finance/reports/config");
  return normalizeReportConfig(response.data);
};

export const saveFinanceReportConfig = async (
  config: Omit<FinanceReportConfig, "configured">
): Promise<FinanceReportConfig> => {
  const response = await axiosInstanceWebsite.put<FinanceReportConfig>("/finance/reports/config", config);
  return normalizeReportConfig(response.data);
};

export const previewFinanceReport = async (
  request: FinanceReportRequest
): Promise<FinanceReportPreview> => {
  const response = await axiosInstanceWebsite.post<FinanceReportPreview>(
    "/finance/reports/preview",
    buildReportPayload(request)
  );
  return response.data;
};

export const getFinanceManualLogistics = async (
  funnelId: string
): Promise<FinanceManualLogisticsConfig> => {
  const response = await axiosInstanceWebsite.get<FinanceManualLogisticsConfig>(
    `/finance/funnels/${encodeURIComponent(funnelId)}/manual-logistics`
  );
  return response.data;
};

export const saveFinanceManualLogistics = async (
  funnelId: string,
  config: Omit<FinanceManualLogisticsConfig, "configured" | "funnel_id">
): Promise<FinanceManualLogisticsConfig> => {
  const response = await axiosInstanceWebsite.put<FinanceManualLogisticsConfig>(
    `/finance/funnels/${encodeURIComponent(funnelId)}/manual-logistics`,
    config
  );
  return response.data;
};

export const sendFinanceReportTest = async (
  request: FinanceReportRequest
): Promise<FinanceReportPreview> => {
  const response = await axiosInstanceWebsite.post<FinanceReportPreview>(
    "/finance/reports/send-test",
    buildReportPayload(request)
  );
  return response.data;
};

const emptyLogisticsOverview = (period: Period): FinanceLogisticsOverview => ({
  dropi_connected: false,
  dropi_status: "unavailable",
  period,
  summary: {
    total_orders: 0,
    dropi_orders: 0,
    matched_orders: 0,
    unmatched_orders: 0,
    matched_value: 0,
    unmatched_value: 0,
    matched_product_cost: 0,
    unmatched_product_cost: 0,
    matched_shipping_cost: 0,
    unmatched_shipping_cost: 0,
    with_guide: 0,
    in_transit: 0,
    delivered: 0,
    returned: 0,
    incidents: 0,
    delivery_rate: 0,
    return_rate: 0,
    value_at_risk: 0,
    product_cost: 0,
    shipping_cost: 0,
    fulfillment_cost: 0,
    platform_commission: 0,
    return_cost: 0,
    variable_cost: 0,
    currency: "COP",
  },
  status_breakdown: [
    { id: "pending", label: "Pendiente", count: 0, value: 0 },
    { id: "guide_generated", label: "Guía generada", count: 0, value: 0 },
    { id: "in_transit", label: "En tránsito", count: 0, value: 0 },
    { id: "delivered", label: "Entregado", count: 0, value: 0 },
    { id: "returned", label: "Devuelto", count: 0, value: 0 },
    { id: "incident", label: "Novedad", count: 0, value: 0 },
    { id: "error", label: "Error", count: 0, value: 0 },
  ],
  map_orders: [],
  map_bubbles: [],
  orders: [],
  carrier_sources: [],
});

export const getFinanceLogisticsOverview = async (
  period: Period
): Promise<FinanceLogisticsOverview> => {
  try {
    const response = await axiosInstanceWebsite.get<FinanceLogisticsOverview>(
      "/finance/logistics/overview",
      { params: buildPeriodParams(period) }
    );
    const fallback = emptyLogisticsOverview(period);
    return {
      ...fallback,
      ...response.data,
      summary: {
        ...fallback.summary,
        ...(response.data.summary ?? {}),
      },
      status_breakdown: response.data.status_breakdown ?? fallback.status_breakdown,
      map_orders: response.data.map_orders ?? [],
      orders: response.data.orders ?? [],
      carrier_sources: response.data.carrier_sources ?? [],
    };
  } catch {
    return emptyLogisticsOverview(period);
  }
};

export const financeService = {
  getFunnels,
  getMetaCampaigns,
  getFinanceMetaAdAccounts,
  getFinanceCampaignPerformance,
  getFinanceAdsPerformance,
  updateFinanceMetaAdAccount,
  matchCampaignsToFunnel,
  saveFinanceFixedCosts,
  previewFinanceFixedCostsImport,
  sendFinanceAssistantMessage,
  getFinanceAssistantConversations,
  getFinanceAssistantConversation,
  previewFinanceReport,
  sendFinanceReportTest,
  getFinanceReportConfig,
  saveFinanceReportConfig,
  getFinanceManualLogistics,
  saveFinanceManualLogistics,
  async startMetaBackfill(): Promise<FinanceMetaBackfillResponse> {
    const response = await axiosInstanceWebsite.post<FinanceMetaBackfillResponse>(
      "/finance/meta/backfills"
    );
    return response.data;
  },
  async startDropiBackfill(period?: Period): Promise<FinanceDropiBackfillResponse> {
    const response = await axiosInstanceWebsite.post<FinanceDropiBackfillResponse>(
      "/finance/dropi/backfills",
      null,
      period ? { params: buildPeriodParams(period) } : undefined
    );
    return response.data;
  },
  async listMetaCampaigns(adAccountId?: string): Promise<FinanceCampaignRow[]> {
    if (!adAccountId) return [];

    try {
      const response = await axiosInstanceWebsite.get(`/meta/campaigns/${adAccountId}/ads`, {
        params: { date_preset: "last_30d" },
      });
      const ads = response.data?.ads;
      if (!Array.isArray(ads)) return [];
      return (ads as MetaAdRow[]).map((row) => {
        const spend = Number(row.spend ?? 0);
        const purchases = Number(row.purchases ?? row.results ?? 0);
        const roas = Number(row.roas ?? 0);
        return {
          id: row.ad_id ?? row.ad_name ?? "meta-row",
          name: row.campaign_name ?? row.ad_name ?? "Campaña sin nombre",
          status: row.effective_status === "ACTIVE" ? "ACTIVE" : row.effective_status === "PAUSED" ? "PAUSED" : "OTHER",
          spend,
          revenue: roas > 0 ? spend * roas : 0,
          purchases,
          roas,
          cxc: Number(row.cost_per_result ?? (purchases > 0 ? spend / purchases : 0)),
          ctr: Number(row.ctr ?? 0),
          lastActivity: row.updated_time ?? "",
        };
      });
    } catch {
      return [];
    }
  },
};
